import { TabItem } from "@/components/CustomTabs";
import { ASSET_GENERATION_TYPES, PROJECT_TYPES, SORT_ORDER, STAGE_NAMES } from "@/constants/constants";
import { ReactNode } from "react";
import { ACTION_TYPES } from "./constants";
import { StepTwoData } from "../concepting/components/StepTwo/types";
import { Concept } from "../concepting/components/StepThree/types";
import {
  Images,
  ModelOutputs,
  RaiMessageFormat,
  StepFourData,
  StepFourLanguageData,
  VideoScript,
} from "../concepting/components/StepFour/types";
import { GeneratedContentPdpCopy, PdpCopyResponse, ProjectCopy, ProjectDataPayload } from "../pdp-copy/components/ProjectCopyList/types";
import { GeneratedDescription } from "@/service/entity/rai-check/rai-check.service";
import { ProjectMetaProps, RAI_STATUS, RAIResponse, statusType } from "@/types";
import { RaiFailedCompliancesProps } from "@/components/RaiErrorAccordion";

interface SavedProjectRaiMessageFormat extends RaiMessageFormat {
  assetUrl?: string;
  assetText?: string;
}

type RAI_DATA_DB = {
  generatedDescription?: GeneratedDescription;
};
export interface RaiFailedCompliance {
  complianceId: string;
  complianceName: string;
  complianceDescription: string;
  validationResult: string;
  failureReasons?: string;
  recommendations?: string;
  triggerTerms?: string[];
}
export interface SavedProjectRAIResponse {
  assetText?: string;
  assetType?: string;
  errorMessage?: string[];
  errorMessages?: string[];
  assetUrl?: string;
  successMessage?: string;
  complianceIds?: string[];
  status?: string;
  xRequestIdRaiCheck?: string;
  failedCompliances?: RaiFailedCompliance[];
}
export interface DBRaiOutputsCheck {
  description: SavedProjectRaiMessageFormat;
  image: SavedProjectRaiMessageFormat;
  videoScript: SavedProjectRaiMessageFormat;
  images?: SavedProjectRAIResponse[];
  content?: SavedProjectRAIResponse;
  captions?: SavedProjectRAIResponse[];
}
export interface Project extends BaseRow {
  id: string;
  name: string | ReactNode;
  displayName?: string | ReactNode;
  type?: string;
  actions?: string[];
  createdAt?: string;
  isFavorite?: boolean;
  unarchive?: ReactNode;
  checkbox?: ReactNode;
  tabType: TabType;
  assetName?: string | ReactNode;
  version?: number;
  defaultType: string;
  isGeneratedOutputs?: boolean;
  videoUrl?: string;
  transcript?: string;
  downloadAll?: ReactNode;
}

export interface Column<T extends Project> {
  key: keyof T;
  label: ReactNode | string;
  allowSort?: boolean;
  isSorted?: boolean;
  isInteractiveElement?: boolean;
}

export interface CustomTabsProps {
  tabs: TabItem[];
  defaultValue?: string;
  className?: string;
}

export interface BaseRow {
  id: string;
  actions?: string[];
  isFavorite?: boolean;
}

export type TabType = "projects" | "assets" | "archive";

export interface RowsState {
  projects: Project[];
  assets: Asset[];
  archive: Project[];
}

export interface DynamicTableProps<T extends Project> {
  data: T[];
  columns: Column<T>[];
  onSort: (columnKey: keyof T) => void;
  type: TabType;
  onRowClick: (id: string, type: TabType) => void;
  selectedRowIds: string[];
  areRowsSelected?: boolean;
  onFavoriteClick?: (id: string, isFavorite?: boolean) => void;
  handleProjectAction?: (action: ActionType, projectId: string, type: TabType) => void;
  setSelectedAssetId?: React.Dispatch<React.SetStateAction<string>>;
  selectedAssetId?: string;
  enableRaiCache?: boolean;
  activeTab: string;
}
export type TabKey = "savedProjects" | "savedAssets" | "archived";

export type SavedWorkImage = {
  jpegURL: string;
  pngURL: string;
  psdURL?: string;
  isEdited?: boolean;
  label?: string;
  isHidden?: boolean;
  sizes?: {
    [key: string]: Images;
  };
};

export type SavedWorkDescription = {
  content: string[];
};

export type SubAssetsType = {
  id: string;
  name: string;
  createdAt: string;
  tabType?: TabType;
  subAssets?: SubAssetsType[];
  image?: SavedWorkImage;
  description?: SavedWorkDescription;
  videoScript?: VideoScript;
  language?: string;
  videoUrl?: string;
  videoFileName?: string;
  raiStatus?: string;
  raiFailedCompliances?: RaiFailedCompliance[];
};

export interface SavedVideoModalProps {
  isOpen: boolean;
  onClose: (text?: string) => void;
  videoUrl: string;
  videoFileName?: string;
  projectName: string;
  version?: number;
  projectMeta: ProjectMetaProps;
  raiStatus?: string;
  raiFailedCompliances?: RaiFailedCompliancesProps[];
  modalTitle?: string;
  asset?: Asset;
  projectId?: string;
}
export interface ViewAssetMetadata {
  projectId?: string;
  productType?: string;
  assetType?: string;
  raiStatus?: string;
  raiFailedCompliances?: RaiFailedCompliancesProps[];
  videoFileName?: string;
}

export type Asset = {
  id: string;
  isOriginal?: boolean;
  isEdited?: boolean;
  name: string | ReactNode;
  createdAt?: string;
  subAssets: SubAssetsType[];
  tabType: TabType;
  assetName?: string | ReactNode;
  image?: SavedWorkImage;
  images?: SavedWorkImage[];
  type: string;
  description?: SavedWorkDescription;
  videoScript?: VideoScript;
  projectName: string;
  version?: number;
  assetIdDescription?: string;
  assetIdVideoScript?: string;
  assetIdContent?: string;
  copyData?: GeneratedContentPdpCopy;
  defaultType: string;
  textType?: string;
  language?: string;
  sizeOption?: string;
  aspectRatio?: string;
  projectOutputId?: string;
  videoUrl?: string;
  videoFileName?: string;
  raiStatus?: string;
  raiFailedCompliances?: RaiFailedCompliance[];
  productType?: string;
  imageDeliverables?: string;
  checkbox?: string;
  isFullyGeneratedImage?: boolean;
  transcript?: string;
  transcriptRAIStatus?: RAI_STATUS;
  transcriptRAIFailedCompliances?: RaiFailedCompliancesProps[];
  isTranscriptFailed?: boolean;
  content?: string;
  downloadAll?: ReactNode;
  generated_outputs_rai_check?: DBRaiOutputsCheck;
  totalScenes?: number;
};

export interface Pagination {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export interface PaginationSavedWork {
  projects: Pagination;
  assets: Pagination;
  archive: Pagination;
}

export interface SavedWorkData {
  projects: Project[];
  savedAssets: Asset[];
  archive: Project[];
  totalProjects: number;
  pagination: PaginationSavedWork;
  allSavedProjectsData?: Project[];
}

export interface SavedWorkContextType {
  savedWorkData: SavedWorkData;
  setSavedWorkData: React.Dispatch<React.SetStateAction<SavedWorkData>>;
}

export interface ReferenceImage {
  url: string;
  fileId: string;
  fileName: string;
  base64?: string;
}

export interface RaiInputProps {
  errorMessages: string[];
  status: string;
  complianceIds?: string[];
}
export interface RaiCheck {
  imageNotesOrComments?: RaiInputProps;
  referenceImageUrl?: RaiInputProps[];
  textInput?: RaiInputProps;
  xRequestIdRaiCheck?: string;
  referenceImageDescription?: RaiInputProps;
}
export interface ImageDataPayload {
  gtins: string[];
  aspectRatio?: string;
  imageNotesOrComments?: string;
  promptText?: string;
  referenceImage: ReferenceImage[];
  imageDeliverables: string;
  raiChecks?: RaiCheck[];
  isImageOnText?: boolean;
  isLogoOnImage?: boolean;
  textInput?: string;
  productPlacementOption?: string;
}

export interface CreatedDateDB {
  _seconds: number;
  _nanoseconds: number;
}
export interface ItemWithCreatedAt {
  createdAt?: CreatedDateDB | string | Date;
  [key: string]: unknown;
}
export interface AllProjectOutputs {
  language?: string;
  id: string;
  inputConcept: {
    gtins: string[];
    aspectRatio: string;
    imageDeliverables?: string;
  };
  modelOutputs: {
    languages: Record<string, GeneratedContentPdpCopy>;
    type: string;
    images: SavedWorkImage[];
    generatedDescription?: GeneratedContentPdpCopy;
  };
  media: {
    images: SavedMedia[];
  };
  createdAt: CreatedDateDB;
  projectVersion?: string;
  assetIdDescription?: string;
  generated_outputs_rai_check: RAI_DATA_DB;
}

export interface ResponseData extends Project {
  id: string;
  userId: string;
  name: string;
  type: string;
  brand: string;
  inputSource?: string;
  isArchived: boolean;
  isFavorite: boolean;
  stage: (typeof STAGE_NAMES)[keyof typeof STAGE_NAMES];
  assetType: string;
  channel: string;
  noOfOutputs: number;
  media: {
    images: EditedImagesData;
  };
  imageDimensions: string;
  projectImageList: ImageDataPayload;
  allProjectOutputs: AllProjectOutputs[];
  uploadedExcelData: PdpCopyResponse;
  campaign_details_review: StepTwoData;
  concept_selection: Concept;
  generated_outputs: StepFourData;
  generated_outputs_rai_check: DBRaiOutputsCheck;
  productPlacementImageUrl?: string;
  productPlacementImagePPType?: string;
  productPlacementImageDocId?: string;
  assetSelection: { script: string; assetType: string; text: string; aspectRatio: string; isImageIncluded: boolean };
  brief_upload: {
    uploadedFileUrl: string;
  };
  projectOptions: { aspectRatio: string };
  projectCopyList: {
    programRationaleConsumerFacing: string;
    programRationalConsumerFacing?: string;
    raiChecks: ProjectDataPayload[];
  } & ProjectCopy;
}

type OmitKeys = "generated_outputs";

export type AssetTypesDb = (typeof ASSET_GENERATION_TYPES)[keyof typeof ASSET_GENERATION_TYPES];
export type PROJECT_TYPE_KEYS = (typeof PROJECT_TYPES)[keyof typeof PROJECT_TYPES];

export interface ResponseDataLanguage extends Omit<ResponseData, OmitKeys> {
  language: string;
  generated_outputs: StepFourLanguageData;
}
export interface VideoRaiCheck {
  description?: SavedProjectRAIResponse;
  firstFrameImage?: SavedProjectRAIResponse;
  lastFrameImage?: SavedProjectRAIResponse;
}
export interface VideoProjectInputs {
  description?: string;
  firstFrameImage?: ReferenceImage;
  lastFrameImage?: ReferenceImage;
  referenceImage?: ReferenceImage[];
  isCheckedFirstFrame?: boolean;
  isCheckedLastFrame?: boolean;
  raiChecks?: VideoRaiCheck[];
}
export interface ProjectImageData {
  url: string;
  fileId: string;
  fileName: string;
  base64?: string;
}
export interface VideoPayload {
  description: string;
  stage: string;
  raiChecks: VideoRaiCheck[];
  firstFrameImage?: ProjectImageData;
  lastFrameImage?: ProjectImageData;
  isCheckedFirstFrame?: boolean;
  isCheckedLastFrame?: boolean;
  isFrameChecked?: boolean;
}

export enum VideoGenerationStatus {
  COMPLETED = "COMPLETED",
  IN_PROGRESS = "IN_PROGRESS",
  NOT_STARTED = "NOT_STARTED",
}

export enum SavedProjectsQuery {
  FROM = "from",
  VALUE = "savedProjects",
}
export interface VideoModelOutputs extends ModelOutputs {
  type?: string;
  videos?: string[];
  videoTaskId?: string;
  videoGenerationStatus?: statusType;
  videoGeneratedAt?: {
    _seconds: number;
    _nanoseconds: number;
  };
  transcript?: string;
}

export interface VideoGeneratedOutputs {
  modelOutputs?: VideoModelOutputs;
}

export interface VideoProjectData {
  id: string;
  name: string;
  resolution?: string;
  aspectRatio?: string;
  inputs?: VideoProjectInputs;
  videoProjectInputs?: VideoProjectInputs;
  generated_outputs?: VideoGeneratedOutputs;
  generated_outputs_rai_check?: {
    images?: SavedProjectRAIResponse[];
    transcript?: SavedProjectRAIResponse;
  };
  version?: number;
  videoDuration?: number;
  brand?: string;
}
export interface StoryboardScene {
  frameUrl: string;
  sceneId: string;
  caption?: string;
  assetIdCaption?: string;
  brand?: string;
  captionVersion?: number;
  aspectRatio?: string;
  status?: string;
}

export type SortableFields = "name" | "createdAt" | "priority" | "type";

export interface SortingQueryParams {
  onlyArchived?: boolean;
  includeAllGeneratedOutputs?: boolean;
  orderBy?: SortableFields;
  sortOrder?: typeof SORT_ORDER.ASC | typeof SORT_ORDER.DESC;
  page?: number;
  limit?: number;
  type?: string;
  isSaveAndClose?: boolean;
}

export type ActionType = keyof typeof ACTION_TYPES;

export enum TAB_TYPES {
  PROJECTS = "projects",
  ASSETS = "assets",
  ARCHIVE = "archive",
}

export type SubAssetImage = {
  id: string;
  name: string;
  createdAt: string;
  image: SavedWorkImage;
  aspectRatio?: string;
  projectOutputId?: string;
};

interface BaseGroup<T> {
  name: string;
  createdAt: string;
  subAssets: T[];
}

export interface DeliverableGroup extends BaseGroup<SubAssetImage> {
  createdAtUnix: number;
}

export interface GtinGroup extends BaseGroup<DeliverableGroup> {
  id: string;
}

export type GtinCopyGroup = {
  createdAt: string;
  subItem: AllProjectOutputs;
};

// Interface for saved media data
export interface SavedMedia {
  id: string;
  projectId: string;
  projectOutputId: string;
  label: string;
  rootMediaId: string | null;
  previousMediaId: string | null;
  raiChecks: RaiFailedCompliancesProps[];
  raiCheckInputPrompt: RAIResponse;
  type: string;
  aspectRatio: string;
  rawImage: SavedWorkImage;
  selectedSizeOption: string;
  sizes: {
    [key: string]: SavedWorkImage;
  };
  inputPrompt: string;
  createdAt: CreatedDateDB;
  meta: {
    index: number;
    numberOfEdits?: number;
    isSaved: boolean;
  };
  updatedAt: CreatedDateDB;
}

export interface EditedImagesData {
  [key: string]: {
    rootMediaId: string;
    latestMediaId: string;
    savedMediaIds: string[];
    savedMedia: SavedMedia[];
  };
}

export interface LeafAsset {
  id: string;
  name: string;
  createdAt: string;
  seconds: number;
  image: SavedWorkImage;
  aspectRatio: string;
  projectOutputId: string;
}
